import React, { useState, useCallback, useEffect } from "react";
import useIsMobile from "../../hooks/useIsMobile";

/**
 * ExpandableTable — generic expandable-row table component.
 *
 * Props:
 *   rows                array of objects (each must have a unique `_id`)
 *   columns             array of { key, label, width?, align?, render?(value, row) }
 *   renderDetail        (data, helpers) => React node
 *                       data = row | draft (draft when editing)
 *                       helpers = { isEditing, startEdit, cancelEdit, saveEdit,
 *                                   updateDraft, saving }
 *   onSave              async (id, draft) => void — called by saveEdit
 *   keyField            string — defaults to "_id"
 *   emptyText           string — shown when rows is empty
 *   stickyHeader        boolean
 *   className           string — extra class on <table>
 *   initialExpandedId   D-T51b — id que se debe expandir automáticamente
 *   initialEditingId    D-T51b — si coincide con un row, abre form de edit
 *   highlightIds        D-T51b — array de ids con highlight (modo compare)
 */
const ExpandableTable = ({
  rows = [],
  columns = [],
  renderDetail,
  onSave,
  keyField = "_id",
  emptyText = "Sin registros",
  stickyHeader = false,
  className = "",
  initialExpandedId = null,
  initialEditingId = null,
  highlightIds = null,
}) => {
  // F4-QA1: acordeón global — solo 1 row abierta a la vez.
  const [expandedId, setExpandedId] = useState(null);
  const [editingRows, setEditingRows] = useState(new Set());
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const toggleExpand = useCallback(id => {
    setExpandedId(prev => {
      if (prev === id) {
        // cerrando el actual → cerrar también su edit
        setEditingRows(e => { const n = new Set(e); n.delete(id); return n; });
        setDrafts(d => { const n = { ...d }; delete n[id]; return n; });
        return null;
      }
      // abriendo otro → cerrar edit del anterior si lo había
      if (prev != null) {
        setEditingRows(e => { const n = new Set(e); n.delete(prev); return n; });
        setDrafts(d => { const n = { ...d }; delete n[prev]; return n; });
      }
      return id;
    });
  }, []);

  const startEdit = useCallback((id, original) => {
    setDrafts(d => ({ ...d, [id]: JSON.parse(JSON.stringify(original)) }));
    setEditingRows(e => { const n = new Set(e); n.add(id); return n; });
    setExpandedId(id);
  }, []);

  const cancelEdit = useCallback(id => {
    setEditingRows(e => { const n = new Set(e); n.delete(id); return n; });
    setDrafts(d => { const n = { ...d }; delete n[id]; return n; });
  }, []);

  const updateDraft = useCallback((id, updater) => {
    setDrafts(d => ({
      ...d,
      [id]: typeof updater === "function" ? updater(d[id]) : { ...d[id], ...updater },
    }));
  }, []);

  const saveEdit = useCallback(async id => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      await onSave?.(id, drafts[id]);
      setEditingRows(e => { const n = new Set(e); n.delete(id); return n; });
      setDrafts(d => { const n = { ...d }; delete n[id]; return n; });
    } catch (e) {
      // Auditoría 28-ago: antes el try/finally SIN catch se tragaba cualquier
      // error (403, validación) y el usuario creía que guardó. Ahora se avisa
      // y la edición queda ABIERTA para reintentar o cancelar.
      alert(`No se pudo guardar: ${e?.response?.data?.error || e.message}`);
    } finally {
      setSaving(s => { const n = { ...s }; delete n[id]; return n; });
    }
  }, [drafts, onSave]);

  // D-T51b — Sincronizar expandedId/editing con props (links desde /pulido).
  // Solo aplica cuando la prop está presente y la fila existe en rows. Una vez
  // aplicado para un (id, edit) determinado, no se vuelve a forzar para no
  // bloquear la interacción manual del usuario.
  const lastAppliedKey = React.useRef(null);
  useEffect(() => {
    if (!initialExpandedId) return;
    // F5.6 D-T59b — String() defensivo para tolerar _id no-string (ObjectId-like).
    const target = String(initialExpandedId);
    const row = rows.find(r => String(r[keyField]) === target);
    if (!row) return;
    // Usar el id como viene en row para que coincida con lo que se setea
    // en setExpandedId (consistencia interna del componente).
    const rowId = row[keyField];
    const key = `${target}::${String(initialEditingId) === target ? "edit" : "view"}`;
    if (lastAppliedKey.current === key) return;
    lastAppliedKey.current = key;

    setExpandedId(rowId);
    if (String(initialEditingId) === target) {
      setDrafts(d => ({ ...d, [rowId]: JSON.parse(JSON.stringify(row)) }));
      setEditingRows(e => { const n = new Set(e); n.add(rowId); return n; });
    }
  }, [initialExpandedId, initialEditingId, rows, keyField]);

  const colCount = columns.length + 1; // +1 for chevron col
  const highlightSet = React.useMemo(
    () => (highlightIds && highlightIds.length ? new Set(highlightIds.map(String)) : null),
    [highlightIds]
  );

  // ── Modo TARJETAS para teléfono (26-ago, decisión Maxi: la versión móvil
  // puede ser distinta a la web — simple y navegable). En vez de la tabla
  // ancha con scroll lateral, cada fila es una tarjeta apilada: las primeras
  // 2 columnas arman el título y el resto va como pares etiqueta→valor.
  // El detalle expandido (renderDetail, con su edición) ocupa el ancho
  // completo — misma lógica de estado, solo cambia el envoltorio visual.
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "var(--bini-text-muted)" }}>{emptyText}</div>
        )}
        {rows.map(row => {
          const id = row[keyField];
          const isExpanded = expandedId === id;
          const isEditing = editingRows.has(id);
          const draft = drafts[id];
          const isSaving = saving[id] ?? false;
          const displayData = isEditing ? draft : row;
          const helpers = {
            isEditing, isSaving,
            startEdit: () => startEdit(id, row),
            cancelEdit: () => cancelEdit(id),
            saveEdit: () => saveEdit(id),
            updateDraft: updater => updateDraft(id, updater),
          };
          const isHighlighted = highlightSet?.has(String(id));
          const [colTitulo, ...colResto] = columns;
          return (
            <div
              key={id}
              data-row-id={id}
              className="t-02"
              style={{
                // Lenguaje móvil 26-ago: tarjeta radio 12, borde por tono
                // (azul suave = expandida, ámbar = destacada), sin sombras.
                background: isExpanded ? "#262626" : "#242424",
                border: `1px solid ${isHighlighted ? "#f39c12" : isExpanded ? "#3a5a7a" : "#2e2e2e"}`,
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <div
                onClick={() => toggleExpand(id)}
                className={isExpanded ? "" : "tap-card"}
                style={{ padding: "12px 12px", cursor: "pointer", minHeight: 48 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="bini-chevron" style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0, textAlign: "center" }}>›</span>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "#fff" }}>
                    {colTitulo && (colTitulo.render ? colTitulo.render(row[colTitulo.key], row) : row[colTitulo.key])}
                  </div>
                </div>
                {colResto.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 8, paddingLeft: 22 }}>
                    {colResto.map(col => {
                      const contenido = col.render ? col.render(row[col.key], row) : row[col.key];
                      if (contenido === null || contenido === undefined || contenido === "") return null;
                      return (
                        <div key={col.key} style={{ fontSize: 12, minWidth: 0, display: "flex", gap: 5, alignItems: "baseline" }}
                          onClick={col.stopPropagation ? e => e.stopPropagation() : undefined}>
                          <span style={{ fontSize: 9, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".04em", flexShrink: 0 }}>{col.label}</span>
                          <span style={{ minWidth: 0, color: "#b8b8b8" }}>{contenido}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {isExpanded && (
                <div className="bini-expand" data-edit-form={isEditing ? id : undefined}
                  style={{ borderTop: "1px solid #2e2e2e", position: "static", width: "100%", maxWidth: "100%" }}>
                  {renderDetail?.(displayData, helpers)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <table className={`bini-table ${className}`} style={{ width: "100%" }}>
      <thead>
        <tr>
          <th style={{ width: 32 }} />
          {columns.map(col => (
            <th
              key={col.key}
              style={{ width: col.width, textAlign: col.align || "left" }}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td
              colSpan={colCount}
              style={{ textAlign: "center", padding: "24px", color: "var(--bini-text-muted)" }}
            >
              {emptyText}
            </td>
          </tr>
        )}
        {rows.map(row => {
          const id = row[keyField];
          const isExpanded = expandedId === id;
          const isEditing = editingRows.has(id);
          const draft = drafts[id];
          const isSaving = saving[id] ?? false;
          const displayData = isEditing ? draft : row;

          const helpers = {
            isEditing,
            isSaving,
            startEdit: () => startEdit(id, row),
            cancelEdit: () => cancelEdit(id),
            saveEdit: () => saveEdit(id),
            updateDraft: updater => updateDraft(id, updater),
          };

          const isHighlighted = highlightSet?.has(String(id));
          return (
            <React.Fragment key={id}>
              <tr
                className="main-row"
                data-row-id={id}
                onClick={() => toggleExpand(id)}
                style={{
                  cursor: "pointer",
                  ...(isHighlighted ? { boxShadow: "inset 0 0 0 2px #f39c12" } : {}),
                }}
              >
                <td>
                  <span
                    className="bini-chevron"
                    style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
                  >
                    ›
                  </span>
                </td>
                {columns.map(col => (
                  <td
                    key={col.key}
                    style={{ textAlign: col.align || "left" }}
                    onClick={col.stopPropagation ? e => e.stopPropagation() : undefined}
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
              {isExpanded && (
                <tr className="expand-row">
                  <td colSpan={colCount} style={{ padding: 0 }}>
                    <div className="bini-expand" data-edit-form={isEditing ? id : undefined}>
                      {renderDetail?.(displayData, helpers)}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export default ExpandableTable;
